import { ofType } from 'redux-observable';
import { isObservable, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { NotificationService } from 'ui-components/services/notification-service';

import * as Actions from './pusher.actions';
import * as AuthConstants from '../../authentication/constants';
import LogoutService from '../../authentication/logout/logout-service';
import { MultiSessionService } from '../../services/auth0/multi-session-service';
import { CookieService } from '../../services/cookie-service';
import { IdentityVerificationService } from '../../services/identity-verification-service';
import { AutomationRulesNotificationService } from '../../services/job-complete-message-services/automation-rules-notification-service';
import { JobCompleteMessageService } from '../../services/job-complete-message-services/job-complete-message-serivce';
import { LocalStorageService } from '../../services/local-storage-service';
import { ProductDetailsService } from '../../services/product-details.service';
import { PusherService } from '../../services/pusher-service/index';
import { normalizePusherMessageData } from '../../services/pusher-service/normalize-pusher-message';
import * as PusherMessages from '../../services/pusher-service/pusher-contract-messages';
import { CONNECT_REQUEST_REFRESH } from '../../services/pusher-service/pusher-contract-messages';
import { SellerMaintenanceNotificationService } from '../../services/seller-maintenance-notification-service';
import { shipstationConnect } from '../../services/shipstation-connect/';
import * as AdminActions from '../../store/admin/admin.actions';
import * as BatchDetailsActions from '../../store/batches/details/batch.details.actions';
import * as BulkUpdateActions from '../../store/bulk/update/bulk.update.actions';
import * as ExportOrdersActions from '../../store/export/orders/export-orders.actions';
import * as ExportShipmentsActions from '../../store/export/shipments/export-shipments.actions';
import Global from '../../store/global-selectors';
import {
  exportInventoryAsyncSuccess,
  exportInventorySuccess,
  exportLocationsInventoryAsyncSuccess,
  exportTotesAsyncSuccess,
  importInventoryCSVSuccess,
} from '../../store/inventory/inventory.actions';
import * as OrdersActions from '../../store/orders/orders.actions';
import { exportProductsComplete } from '../../store/products/grid/product.grid.actions';
import { exportPurchaseOrdersAsyncSuccess } from '../../store/purchase-orders/purchase-orders.actions';
import * as SellerActions from '../../store/seller/seller.actions';
import { getSeller, refreshLabelProviders } from '../../store/seller/seller.actions';
import { showWalletProviderApprovalGrantedNotification } from '../../wallet/messages/wallet-notifications';
import { displayLocalDeliveryAlert } from '../alerts/alerts.observables';
import { bindToPusherEvents, getIsJobComplete } from '../bulk/update/pusher-helpers';
import { entitiesPatchAlternativeRates } from '../entities/entities.actions';

export const pusherSellerChannelSubscribed = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SELLER_CHANNEL_SUBSCRIBE_SUCCESS),
    mergeMap(() => {
      const state = getState();
      const { userId, username } = Global.selectUserCurrentUserInfo(state);
      const sellerHomeCountry = Global.selectSellerHomeCountry(state);
      const pusherMessage$ = new Observable((observer) => {
        PusherService.bindToEvent(PusherMessages.STORE_REFRESH_STATUS, (data) => {
          observer.next(SellerActions.updateStoreAfterRefresh(normalizePusherMessageData(data)));
        });

        PusherService.bindToEvent(PusherMessages.ADDRESS_UPDATED, (data) => {
          observer.next(BatchDetailsActions.addressesUpdatedResponse(data));
        });

        const handleBulkMessage = (data) => {
          const { messageGuid } = data;
          if (getIsJobComplete(data)) {
            observer.next(BulkUpdateActions.bulkUpdateJobComplete(messageGuid));
          }
        };
        bindToPusherEvents(handleBulkMessage);

        PusherService.bindToEvent(PusherMessages.BULK_ACTION_SALES_ORDER_CANCEL, ({ messageGuid, data }) => {
          observer.next(OrdersActions.bulkCancelJobComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.EXPORT_SHIPMENTS_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportShipmentsActions.exportShipmentsComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.CUSTOM_EXPORT_SHIPMENTS_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportShipmentsActions.exportShipmentsComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.CUSTOM_EXPORT_SHIPMENTS_QUICKSEARCH_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportShipmentsActions.exportShipmentsComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.EXPORT_SALES_ORDER_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportOrdersActions.exportOrdersComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.EXPORT_FULFILLMENT_PLAN_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportOrdersActions.exportOrdersComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.EXPORT_INVENTORY_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(exportInventoryAsyncSuccess(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.EXPORT_LOCATIONS_INVENTORY_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(exportLocationsInventoryAsyncSuccess(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.CSV_TOTE_EXPORT_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(exportTotesAsyncSuccess(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.PURCHASE_ORDER_EXPORT_COMPLETE, ({ messageGuid, data }) => {
          observer.next(exportPurchaseOrdersAsyncSuccess(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.CUSTOM_EXPORT_ORDERS_JOB_COMPLETE, ({ messageGuid, data }) => {
          observer.next(ExportOrdersActions.exportOrdersComplete(messageGuid, data));
        });
        PusherService.bindToEvent(PusherMessages.DROPIN_EXPIRATION, (data) => {
          if (data.Username === username) {
            observer.next(AdminActions.endDropin());
          }
        });
        PusherService.bindToEvent(PusherMessages.SERVER_STAMPS_VERIFICATION_REQUIRED, (data) => {
          observer.next(SellerActions.startStampsAccountVerification(data));
        });
        PusherService.bindToEvent(PusherMessages.DEACTIVATED_USER, async (data) => {
          if (data.userId === userId) {
            await LogoutService.logout();
          }
        });

        PusherService.bindToEvent(PusherMessages.PRODUCT_DETAILS_APPLY_CHANGES_COMPLETE, (data) => {
          const successAction = ProductDetailsService.handleCompletedApplyToOpenFulfillmentPlansMessage(data);
          if (successAction) {
            observer.next(successAction);
          }
        });

        PusherService.bindToEvent(PusherMessages.RECEIVED_USER_TOKEN_WEBHOOK_RESPONSE, async () => {
          observer.next(getSeller());
        });

        PusherService.bindToEvent(PusherMessages.INVENTORY_IMPORT_JOB_COMPLETE, (importResponse) => {
          observer.next(importInventoryCSVSuccess(importResponse));
        });

        PusherService.bindToEvent(PusherMessages.CORE_JOBSERVICE_JOB_COMPLETE, (data) => {
          if (JobCompleteMessageService.isProductExportMessage(data)) {
            observer.next(exportProductsComplete(data));
          } else if (JobCompleteMessageService.isInventoryExportMessage(data)) {
            observer.next(exportInventorySuccess(data));
          } else {
            JobCompleteMessageService.processMessage(data);
          }
        });

        PusherService.bindToEvent(PusherMessages.REPROCESS_OPEN_ORDERS_BATCH, (data) => {
          AutomationRulesNotificationService.processMessage(data);
        });

        PusherService.bindToEvent(PusherMessages.LOCAL_DELIVERY_EXCEPTION, (data) => {
          observer.next(displayLocalDeliveryAlert(data));
        });

        if (!shipstationConnect.isInitialized()) {
          shipstationConnect.initialize(observer, userId);
          PusherService.push(CONNECT_REQUEST_REFRESH, { userId });
        }

        PusherService.bindToEvent(PusherMessages.ALTERNATIVE_RATES_UPDATED, ({ data }) => {
          observer.next(entitiesPatchAlternativeRates(data));
        });

        PusherService.bindToEvent(PusherMessages.SELLER_PROVIDER_APPROVAL_GRANTED, async (data) => {
          if (data) {
            const { providerId } = data;
            const updateState = () => {
              observer.next(refreshLabelProviders({ shouldRefreshServices: false }));
            };
            await showWalletProviderApprovalGrantedNotification(providerId, sellerHomeCountry, updateState);
          }
        });

        PusherService.bindToEvent(PusherMessages.SELLER_IDV_SUCCEEDED, (data) => {
          const successAction = IdentityVerificationService.handleIdentityVerificationSuccess(data);
          if (successAction) {
            for (let i = 0; i < successAction.length; i++) {
              observer.next(successAction[i]);
            }
          }
        });

        PusherService.bindToEvent(PusherMessages.SELLER_IDV_PENDING_REVIEW, (data) => {
          const pendingAction = IdentityVerificationService.handleIdentityVerificationPending(data);
          if (pendingAction) {
            for (let i = 0; i < pendingAction.length; i++) {
              observer.next(pendingAction[i]);
            }
          }
        });
      });

      PusherService.bindToEvent(PusherMessages.SERVER_PAGE_REDIRECT, (data) => {
        if (data.success && data.location && data.userId === userId) {
          window.location = data.location;
        }
      });
      PusherService.bindToEvent(PusherMessages.SERVER_LOGOUT_AND_REDIRECT, (data) => {
        if (data.success && data.location && data.userId === userId) {
          CookieService.removeV3Config();
          CookieService.removeAuthentication();
          LocalStorageService.clear();
          window.location = data.location;
        }
      });
      PusherService.bindToEvent(PusherMessages.SERVER_LOGOUT, async (data) => {
        if (data.userId === userId) {
          await LogoutService.logout(null, data.message);
        }
      });
      PusherService.bindToEvent(PusherMessages.V2_IFRAME_STORE_AUTH_REDIRECT, (data) => {
        const { redirectUrl } = data;
        const messageUserId = data.userId;
        if (!redirectUrl || !messageUserId || messageUserId !== userId) {
          return;
        }
        window.location.href = redirectUrl;
      });

      PusherService.bindToEvent(PusherMessages.CENTRAL_AUTH_LOGIN, async (data) => {
        const isCurrentUserSysAdmin = Global.selectUserisSysAdmin(state);
        await MultiSessionService.handleCentralAuthLogin(data, isCurrentUserSysAdmin);
      });

      PusherService.bindToEvent(PusherMessages.FORCED_SELLER_LOGOUT, async () => {
        await LogoutService.logout(null, AuthConstants.LogoutTypeMaintenance);
      });

      PusherService.bindToEvent(PusherMessages.SELLER_MAINTENANCE_PENDING, async (data) => {
        SellerMaintenanceNotificationService.processMessage(data);
      });

      PusherService.bindToEvent(PusherMessages.SELLER_MAINTENANCE_IMPENDING, async (data) => {
        SellerMaintenanceNotificationService.processMessage(data);
      });

      PusherService.bindToEvent(PusherMessages.BULK_PRINT_PACKING_SLIPS_FALLBACK, async (message) => {
        if (message.data) {
          const notificationConfig = {
            content: (
              <div
                // oxlint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: message.data.Message }}
              />
            ),
            title: message.data.Title,
            dontHide: true,
            notificationType: 'warning',
          };
          NotificationService.showNotification(notificationConfig);
        }
      });

      PusherService.bindToEvent(PusherMessages.SELLER_IDV_FAILED, async () => {
        await LogoutService.logout(null, AuthConstants.LogoutTypeMaintenance);
      });

      PusherService.bindToEvent(PusherMessages.SELLER_LOCKOUT, async () => {
        await LogoutService.logout(null, AuthConstants.LogoutTypeMaintenance);
      });

      return pusherMessage$.pipe(
        mergeMap((callBackAction) => {
          return isObservable(callBackAction) ? callBackAction : of(callBackAction);
        }),
      );
    }),
  );
};
